import React from 'react';
import { roomGeneratorUrl } from '@/lib/backendUrl';
import { FCreateRoom, UseRoom } from './useRoom';
import { UIConfig } from './message';
import {getRoomFromURL} from './useRoomID';
import { authModeToRoomMode, UseConfig } from './useConfig';
import ShapeHero from './components/backgrounds/ShapeHero';
import ShimmerText from './components/text/ShimmerText';
import { LiquidGlassCard } from './components/glass/LiquidGlassCard';
import { LiquidButton } from './components/glass/LiquidButton';
import { motion, AnimatePresence } from 'framer-motion';
import { ReceiverLobby } from './ReceiverLobby';
import { SenderLobby } from './SenderLobby';
import { getStoredRoomDisplayName } from './roomDisplayName';

const ROTATING_TEXTS = [
    'Share Your Screen',
    'Connect Instantly',
    'Collaborate Seamlessly',
    'Stream in HD',
    'Secure & Private',
];

const CreateRoom = ({
    room,
    config,
    role,
}: Pick<UseRoom, 'room'> & { config: UIConfig; role?: string }) => {
    const isReceiver = role === 'receiver';
    const [id, setId] = React.useState(() =>
        isReceiver ? config.roomName || '' : getRoomFromURL() ?? config.roomName
    );
    const mode = authModeToRoomMode(config.authMode, config.loggedIn);
    const [ownerLeave, setOwnerLeave] = React.useState(config.closeRoomWhenOwnerLeaves);
    const isSender = role === 'sender';

    const submit = () => {
        if (isReceiver) {
            room({
                type: 'create',
                payload: {
                    mode,
                    closeOnOwnerLeave: ownerLeave,
                    joinIfExist: true,
                    id: id || undefined,
                    role: 'receiver',
                },
            });
        } else if (isSender) {
            room({
                type: 'join',
                payload: {
                    id: id || '',
                    role: 'sender',
                },
            });
        } else {
            room({
                type: 'create',
                payload: {
                    mode,
                    closeOnOwnerLeave: ownerLeave,
                    joinIfExist: true,
                    id: id || undefined,
                    role: role,
                },
            });
        }
    };

    return (
        <div className="space-y-4">
            {isReceiver && (
                <>
                    <div className="text-center space-y-2">
                        <h3 className="text-2xl font-semibold text-white">Receiver Mode</h3>
                        <p className="text-white/60 text-sm">
                            Generate a Room ID to receive streams from senders
                        </p>
                    </div>
                    <input
                        type="text"
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                        placeholder="Room ID (leave empty for random)"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 backdrop-blur-sm"
                    />
                    <label className="flex items-center gap-3 text-white/80 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={ownerLeave}
                            onChange={(e) => setOwnerLeave(e.target.checked)}
                            className="w-4 h-4 rounded border-white/20 bg-white/5 text-white focus:ring-white/20"
                        />
                        <span className="text-sm">Close Room after you leave</span>
                    </label>
                </>
            )}
            {isSender && (
                <>
                    <div className="text-center space-y-2">
                        <h3 className="text-2xl font-semibold text-white">Sender Mode</h3>
                        <p className="text-white/60 text-sm">Enter the Room ID to send your stream</p>
                    </div>
                    <input
                        type="text"
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                        placeholder="Room ID"
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 backdrop-blur-sm"
                    />
                </>
            )}
            {!isReceiver && !isSender && (
                <>
                    <input
                        type="text"
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                        placeholder="Room ID"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 backdrop-blur-sm"
                    />
                    <label className="flex items-center gap-3 text-white/80 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={ownerLeave}
                            onChange={(e) => setOwnerLeave(e.target.checked)}
                            className="w-4 h-4 rounded border-white/20 bg-white/5 text-white focus:ring-white/20"
                        />
                        <span className="text-sm">Close Room after you leave</span>
                    </label>
                </>
            )}
            <div className="text-center text-white/60 text-sm">
                Nat Traversal via:{' '}
                <a
                    href="https://inshare.net/#/nat-traversal"
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                >
                    {mode.toUpperCase()}
                </a>
            </div>
            <LiquidButton
                onClick={submit}
                className="w-full bg-gradient-to-b from-gray-600/80 to-gray-700/80 hover:from-gray-500/80 hover:to-gray-600/80 text-white border-0 py-6 text-lg font-semibold shadow-2xl"
            >
                {isReceiver ? 'Generate Room ID' : isSender ? 'Join Room' : 'Create or Join a Room'}
            </LiquidButton>
        </div>
    );
};

export const RoomManage = ({
    room,
    config,
    role,
    state,
    setRoomDisplayName,
}: {
    room: FCreateRoom;
    config: UseConfig;
    role?: string;
    state?: any;
    setRoomDisplayName?: (name: string) => void;
}) => {
    const initialUrlRoomId = getRoomFromURL() || '';
    const [currentTextIndex, setCurrentTextIndex] = React.useState(0);
    const [roomId, setRoomId] = React.useState<string>(initialUrlRoomId);
    const roomCreatedRef = React.useRef(false);
    const mode = authModeToRoomMode(config.authMode, config.loggedIn);

    // Extract room ID from state if available
    React.useEffect(() => {
        if (state && state.id && !roomId) {
            setRoomId(state.id);
        }
    }, [state, roomId]);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTextIndex((prev) => (prev + 1) % ROTATING_TEXTS.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    // Receiver flow: if URL provides room ID, create/join that exact room; otherwise request one from API.
    React.useEffect(() => {
        if (role !== 'receiver' || roomCreatedRef.current) return;

        const createWithRoomId = (finalRoomId: string) => {
            setRoomId(finalRoomId);
            room({
                type: 'create',
                payload: {
                    mode,
                    closeOnOwnerLeave: true,
                    joinIfExist: true,
                    id: finalRoomId,
                    role: 'receiver',
                    displayName: getStoredRoomDisplayName(),
                },
            });
        };

        const roomFromUrl = roomId?.trim();
        if (roomFromUrl) {
            roomCreatedRef.current = true;
            createWithRoomId(roomFromUrl);
            return;
        }

        roomCreatedRef.current = true;
        let cancelled = false;

        (async () => {
            try {
                const response = await fetch(roomGeneratorUrl(), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({ webhookUrl: '' }),
                });
                if (!response.ok) {
                    throw new Error(`generate room failed: ${response.status}`);
                }
                const payload = await response.json();
                const generatedRoomId = String(payload?.roomId || '').trim();
                if (!generatedRoomId) {
                    throw new Error('generate room response missing roomId');
                }
                if (!cancelled) {
                    createWithRoomId(generatedRoomId);
                }
            } catch (err) {
                console.error('Failed to generate room ID for receiver:', err);
                roomCreatedRef.current = false;
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [role, roomId, room, mode]);

    const handleSenderJoin = (_name: string, joinRoomId: string) => {
        room({
            type: 'join',
            payload: {
                id: joinRoomId,
                role: 'sender',
            },
        });
    };

    // Show receiver lobby if room is created but no sender has joined yet
    if (role === 'receiver' && roomId) {
        return (
            <ReceiverLobby
                roomId={roomId}
                roomDisplayName={state?.displayName}
                onRoomDisplayNameSave={setRoomDisplayName}
                onSenderJoined={() => {}} // Router handles navigation
                config={config}
            />
        );
    }

    if (role === 'sender') {
        const urlRoomId = getRoomFromURL();
        return (
            <SenderLobby
                onJoin={handleSenderJoin}
                config={config}
                initialRoomId={urlRoomId || ''}
            />
        );
    }

    // Default flow for non-role-specific users
    return (
        <ShapeHero>
            <div className="flex flex-col items-center justify-center min-h-screen py-8">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-8"
                >
                    <img src="./EllingtonLogo.svg" className="w-48 mx-auto mb-6" alt="logo" />
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentTextIndex}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5 }}
                        >
                            <ShimmerText
                                text={ROTATING_TEXTS[currentTextIndex]}
                                className="text-3xl md:text-5xl"
                            />
                        </motion.div>
                    </AnimatePresence>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="w-full max-w-md"
                >
                    <LiquidGlassCard className="border-white/10 bg-white/5 backdrop-blur-xl">
                        <div className="space-y-6">
                            <div className="text-center">
                                <span className="text-white text-xl font-semibold">
                                    Hello {config.user}!
                                </span>
                            </div>

                            <div className="h-px bg-white/10" />

                            <CreateRoom room={room} config={config} role={role} />
                        </div>
                    </LiquidGlassCard>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-8 text-white/40 text-sm text-center"
                >
                    Ellington {config.version} |{' '}
                    <a
                        href="https://github.com/inshare/server/"
                        className="text-blue-400 hover:text-blue-300 underline"
                    >
                        GitHub
                    </a>
                </motion.div>
            </div>
        </ShapeHero>
    );
};
